import React, { useState } from 'react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  AreaChart,
  Area,
  Legend,
  BarChart,
  Bar
} from 'recharts';
import {
  Paper,
  Typography,
  Grid,
  Box,
  Card,
  CardContent,
  ToggleButton,
  ToggleButtonGroup,
  Avatar,
  Stack,
  Divider,
  Chip,
  LinearProgress
} from '@mui/material';
import {
  SentimentSatisfied as PositiveIcon,
  SentimentDissatisfied as NegativeIcon,
  SentimentNeutral as NeutralIcon,
  TrendingUp as TrendIcon,
  Analytics as AnalyticsIcon
} from '@mui/icons-material';

function SentimentChart({ data, detailed = false }) {
  const [chartType, setChartType] = useState('trend');

  if (!data) {
    return (
      <Paper sx={{ p: 3 }}>
        <Typography variant="h6" color="text.secondary">
          Dati sentiment non disponibili
        </Typography>
      </Paper>
    );
  }

  const sentimentColors = {
    positive: '#4caf50',
    neutral: '#ff9800', 
    negative: '#f44336'
  };

  const sentimentIcons = {
    positive: <PositiveIcon sx={{ color: sentimentColors.positive }} />,
    neutral: <NeutralIcon sx={{ color: sentimentColors.neutral }} />,
    negative: <NegativeIcon sx={{ color: sentimentColors.negative }} />
  };

  // Process sentiment distribution data for pie chart
  const pieData = Object.entries(data.sentiment_distribution || {}).map(([sentiment, count]) => ({
    name: sentiment.charAt(0).toUpperCase() + sentiment.slice(1),
    value: count,
    color: sentimentColors[sentiment] || '#999'
  }));

  // Calculate total for percentages
  const total = data.total_messages_analyzed || pieData.reduce((sum, item) => sum + item.value, 0);

  // Process trend data
  const trendData = data.sentiment_trends || [];

  // Calculate sentiment scores and insights
  const sentimentScore = data.overall_sentiment_score || 0;
  const sentimentTrend = data.sentiment_trend || 0;

  const getSentimentLevel = (score) => {
    if (score > 0.3) return { label: 'Molto Positivo', color: sentimentColors.positive };
    if (score > 0.1) return { label: 'Positivo', color: sentimentColors.positive };
    if (score > -0.1) return { label: 'Neutrale', color: sentimentColors.neutral };
    if (score > -0.3) return { label: 'Negativo', color: sentimentColors.negative };
    return { label: 'Molto Negativo', color: sentimentColors.negative };
  };

  const sentimentLevel = getSentimentLevel(sentimentScore);

  const renderCustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div style={{ 
          backgroundColor: 'white', 
          padding: '10px', 
          border: '1px solid #ccc',
          borderRadius: '4px',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
        }}>
          <p style={{ margin: 0, fontWeight: 'bold' }}>{`Data: ${label}`}</p>
          {payload.map((entry, index) => (
            <p key={index} style={{ margin: 0, color: entry.color }}>
              {`${entry.name}: ${entry.value.toFixed(3)}`}
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  const renderPieTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const data = payload[0];
      const percentage = ((data.value / total) * 100).toFixed(1);
      return (
        <div style={{ 
          backgroundColor: 'white', 
          padding: '10px', 
          border: '1px solid #ccc',
          borderRadius: '4px',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
        }}>
          <p style={{ margin: 0, fontWeight: 'bold', color: data.payload.color }}>
            {data.name}
          </p>
          <p style={{ margin: 0 }}>
            {`Conteggio: ${data.value}`}
          </p>
          <p style={{ margin: 0 }}>
            {`Percentuale: ${percentage}%`}
          </p>
        </div>
      );
    }
    return null;
  };

  return (
    <Paper sx={{ p: 3 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h6" component="h2">
          <AnalyticsIcon sx={{ mr: 1, verticalAlign: 'bottom' }} />
          Analisi Sentiment
        </Typography>
        
        {detailed && (
          <ToggleButtonGroup
            value={chartType}
            exclusive
            onChange={(e, newType) => newType && setChartType(newType)}
            size="small"
          >
            <ToggleButton value="trend">Trend</ToggleButton>
            <ToggleButton value="distribution">Distribuzione</ToggleButton>
            <ToggleButton value="overview">Panoramica</ToggleButton>
          </ToggleButtonGroup>
        )}
      </Box>

      {chartType === 'overview' && (
        <Grid container spacing={3}>
          {/* Overall Sentiment Score */}
          <Grid item xs={12} md={4}>
            <Card variant="outlined">
              <CardContent sx={{ textAlign: 'center' }}>
                <Typography variant="h4" sx={{ color: sentimentLevel.color, fontWeight: 'bold', mb: 1 }}>
                  {sentimentScore.toFixed(3)}
                </Typography>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Score Sentiment Generale
                </Typography>
                <Chip 
                  label={sentimentLevel.label}
                  sx={{ 
                    backgroundColor: sentimentLevel.color + '20',
                    color: sentimentLevel.color,
                    fontWeight: 'bold'
                  }}
                />
              </CardContent>
            </Card>
          </Grid>

          {/* Sentiment Distribution Cards */}
          {pieData.map((item, index) => (
            <Grid item xs={12} md={4} key={index}>
              <Card variant="outlined">
                <CardContent sx={{ textAlign: 'center' }}>
                  <Avatar sx={{ bgcolor: item.color + '20', width: 56, height: 56, mx: 'auto', mb: 2 }}>
                    {sentimentIcons[item.name.toLowerCase()]}
                  </Avatar>
                  <Typography variant="h4" sx={{ color: item.color, fontWeight: 'bold' }}>
                    {item.value}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    {item.name}
                  </Typography>
                  <LinearProgress 
                    variant="determinate" 
                    value={(item.value / total) * 100} 
                    sx={{ 
                      mt: 1, 
                      height: 6, 
                      borderRadius: 3,
                      '& .MuiLinearProgress-bar': {
                        backgroundColor: item.color
                      }
                    }}
                  />
                  <Typography variant="caption" color="text.secondary">
                    {((item.value / total) * 100).toFixed(1)}% del totale
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {chartType === 'trend' && trendData.length > 0 && (
        <Box>
          <Typography variant="subtitle1" gutterBottom>
            Trend Sentiment nel Tempo
          </Typography>
          <ResponsiveContainer width="100%" height={400}>
            <AreaChart data={trendData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis 
                dataKey="timestamp" 
                tickFormatter={(value) => new Date(value).toLocaleDateString()}
              />
              <YAxis domain={[-1, 1]} />
              <Tooltip content={renderCustomTooltip} />
              <Legend />
              <Area
                type="monotone"
                dataKey="positive"
                stackId="1"
                stroke={sentimentColors.positive}
                fill={sentimentColors.positive}
                fillOpacity={0.6}
              />
              <Area
                type="monotone"
                dataKey="neutral"
                stackId="1"
                stroke={sentimentColors.neutral}
                fill={sentimentColors.neutral}
                fillOpacity={0.6}
              />
              <Area
                type="monotone"
                dataKey="negative"
                stackId="1"
                stroke={sentimentColors.negative}
                fill={sentimentColors.negative}
                fillOpacity={0.6}
              />
            </AreaChart>
          </ResponsiveContainer>
        </Box>
      )}

      {chartType === 'distribution' && (
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle1" gutterBottom>
              Distribuzione Sentiment
            </Typography>
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={pieData}
                  cx="50%"
                  cy="50%"
                  outerRadius={100}
                  fill="#8884d8"
                  dataKey="value"
                  label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                >
                  {pieData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip content={renderPieTooltip} />
              </PieChart>
            </ResponsiveContainer>
          </Grid>

          <Grid item xs={12} md={6}>
            <Typography variant="subtitle1" gutterBottom>
              Comparazione Sentiment
            </Typography>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={pieData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" />
                <YAxis />
                <Tooltip />
                <Bar dataKey="value" fill="#8884d8">
                  {pieData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </Grid>
        </Grid>
      )}

      {/* Simple view for non-detailed mode */}
      {!detailed && (
        <ResponsiveContainer width="100%" height={250}>
          <LineChart data={trendData}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis 
              dataKey="timestamp" 
              tickFormatter={(value) => new Date(value).toLocaleDateString()}
            />
            <YAxis domain={[-1, 1]} />
            <Tooltip content={renderCustomTooltip} />
            <Line 
              type="monotone" 
              dataKey="average_sentiment" 
              stroke="#8884d8" 
              strokeWidth={2}
              dot={{ fill: '#8884d8', strokeWidth: 2, r: 4 }}
            />
          </LineChart>
        </ResponsiveContainer>
      )}

      {/* Summary Stats */}
      {detailed && (
        <Box mt={3}>
          <Divider sx={{ mb: 2 }} />
          <Typography variant="subtitle2" gutterBottom>
            Statistiche Sentiment
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={4}>
              <Box textAlign="center">
                <Typography variant="h6" color="primary">
                  {total}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Conversazioni Analizzate
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Box textAlign="center">
                <Typography variant="h6" color="secondary">
                  {sentimentScore.toFixed(3)}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Score Medio
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Box textAlign="center" display="flex" alignItems="center" justifyContent="center" gap={1}>
                <TrendIcon color={sentimentTrend >= 0 ? 'success' : 'error'} />
                <Typography variant="h6" color={sentimentTrend >= 0 ? 'success.main' : 'error.main'}>
                  {sentimentTrend >= 0 ? '+' : ''}{sentimentTrend.toFixed(2)}%
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Trend
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </Box>
      )}
    </Paper>
  );
}

export default SentimentChart;
